import { Palette, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HexColorPicker } from "react-colorful";
import { Link, useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useEditorStore } from "@/store/editorStore";
import { useVariablesStore } from "@/store/variablesStore";
import { toCSSVarReference } from "@/utils/cssVariables";

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  labelClassName?: string;
  labelTooltip?: string;
  onLabelMouseEnter?: () => void;
  onLabelMouseLeave?: () => void;
  onReset?: () => void;
  showReset?: boolean;
  includeOpacity?: boolean;
  opacityValue?: number;
  onOpacityChange?: (opacity: number) => void;
  variant?: "default" | "compact";
  hideIcon?: boolean;
  hideColorVariables?: boolean;
  tightSpacing?: boolean;
}

// Updated color palette as requested
const COLOR_PALETTE = [
  // Column 1
  "#d0e2ff",
  "#a2c6ff",
  "#156ee2",
  "#0d52ac",
  // Column 2
  "#cff5d1",
  "#9be095",
  "#058a0d",
  "#006500",
  // Column 3
  "#efeafd",
  "#ffa6c1",
  "#dc043d",
  "#b10f41",
  // Column 4
  "#e5e9f1",
  "#c6c7cf",
  "#616670",
  "#41454d",
  // Add white and black as presets (transparent is handled separately)
  "#ffffff",
  "#000000",
];

// Function to determine if text should be white or black based on palette position
const getContrastTextColor = (color: string): string => {
  // Special handling for transparent
  if (color === "transparent") {
    return "#000000"; // Black text on transparent background
  }

  const colorIndex = COLOR_PALETTE.indexOf(color);

  if (colorIndex !== -1) {
    // First two colors in each column should have black text
    const columnPosition = colorIndex % 4;
    return columnPosition < 2 ? "#000000" : "#ffffff";
  }

  // Fallback for custom colors - calculate luminance
  const hex = color.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return white for dark colors, black for light colors
  return luminance > 0.5 ? "#000000" : "#ffffff";
};

const ColorPickerComponent = ({
  value,
  onChange,
  label = "Color",
  labelClassName,
  labelTooltip,
  onLabelMouseEnter,
  onLabelMouseLeave,
  onReset,
  showReset = false,
  includeOpacity = false,
  opacityValue = 1,
  onOpacityChange,
  variant = "default",
  hideIcon = false,
  hideColorVariables = false,
  tightSpacing = false,
}: ColorPickerProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { project } = useEditorStore();
  const {
    getCombinedVariables,
    getProjectVariables,
    getCraftspaceVariables,
  } = useVariablesStore();

  // Get combined variables (craftspace + project)
  const combinedVariables =
    projectId && project?.craftspace_id
      ? getCombinedVariables(project.craftspace_id, projectId)
      : { colors: [] };
  const _allColorVariables = combinedVariables.colors || [];

  // Separate craftspace and project colors
  const projectVariables = projectId ? getProjectVariables(projectId) : { colors: [] };
  const craftspaceVariables = project?.craftspace_id
    ? getCraftspaceVariables(project.craftspace_id)
    : { colors: [] };

  const projectColors = projectVariables.colors || [];
  const craftspaceColors = craftspaceVariables.colors || [];

  // Filter out project colors from combined to get only craftspace colors
  const craftspaceOnlyColors = craftspaceColors.filter(
    (wsColor) => !projectColors.some((projColor) => projColor.name === wsColor.name),
  );

  // Internal opacity state to handle real-time updates
  const [internalOpacity, setInternalOpacity] = useState(opacityValue || 0.5);

  // Update internal opacity when external value changes
  useEffect(() => {
    const newOpacity = opacityValue || 0.5;
    setInternalOpacity(newOpacity);
  }, [opacityValue]);

  // Removed complex CSS styling that was interfering with slider functionality
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState(value || "#ffffff");
  const [inputValue, setInputValue] = useState(value || "#ffffff");
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    document.body.classList.toggle("colorpicker-open", isOpen);
    return () => {
      document.body.classList.remove("colorpicker-open");
    };
  }, [isOpen]);
  const [localPickerColor, setLocalPickerColor] = useState(value || "#ffffff");
  const [localPickerOpacity, setLocalPickerOpacity] = useState(1);
  const [localPickerHexInput, setLocalPickerHexInput] = useState("#ffffff");
  const [isLocalAdjusting, setIsLocalAdjusting] = useState(false);
  const [isMainPickerAdjusting, setIsMainPickerAdjusting] = useState(false);
  const [hoverTooltip, setHoverTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    title: string;
    detail: string;
  }>({ visible: false, x: 0, y: 0, title: "", detail: "" });
  const [hoverPreviewBaseValue, setHoverPreviewBaseValue] = useState<string | null>(null);
  const [hoverPreviewColor, setHoverPreviewColor] = useState<string | null>(null);
  const [opacityInput, setOpacityInput] = useState("100");

  const parseColorMixValue = (rawInput: string): { base: string; percent: number } | null => {
    const match = rawInput.match(
      /color-mix\([^,]+,\s*([^,]+)\s+([0-9.]+)%\s*,\s*transparent\s*\)/i,
    );
    if (!match) return null;
    const [, base, percent] = match;
    const parsedPercent = Number.parseFloat(percent);
    if (!Number.isFinite(parsedPercent)) return null;
    return { base: base.trim(), percent: parsedPercent };
  };

  const getDisplayNameForValue = (nextValue: string | undefined): string => {
    if (!nextValue) return "#ffffff";
    const trimmed = nextValue.trim();
    if (trimmed.toLowerCase().startsWith("color-mix(")) {
      const mix = parseColorMixValue(trimmed);
      if (mix) {
        const base = mix.base.trim();
        const varMatch = base.match(/^var\(\s*(--[^)]+)\s*\)$/);
        if (varMatch) {
          return varMatch[1].replace(/^--/, "");
        }
        if (base.startsWith("--")) {
          return base.replace(/^--/, "");
        }
        return base.replace(/^#/, "");
      }
    }
    const varMatch = trimmed.match(/^var\(\s*(--[^)]+)\s*\)$/);
    if (varMatch) {
      return varMatch[1].replace(/^--/, "");
    }
    if (trimmed.startsWith("--")) {
      return trimmed.replace(/^--/, "");
    }
    if (trimmed.startsWith("rgba(") || trimmed.startsWith("rgb(")) {
      const hex = getColorWithoutAlpha(trimmed).replace(/^#/, "");
      const alpha = getAlphaFromColor(trimmed);
      if (!Number.isFinite(alpha) || alpha >= 0.99) return hex;
      return hex;
    }
    if (trimmed.startsWith("#") && trimmed.length === 9) {
      return trimmed.substring(1, 7);
    }
    return trimmed.replace(/^#/, "");
  };

  const isLocalColorValue = (nextValue: string | undefined) => {
    if (!nextValue) return false;
    const trimmed = nextValue.trim();
    const varMatch = trimmed.match(/^var\(\s*(--[^)]+)\s*\)$/);
    if (varMatch) return false;
    if (trimmed.startsWith("--")) return false;
    if (
      projectColors.some((colorVar) => colorVar.name === trimmed) ||
      craftspaceOnlyColors.some((colorVar) => colorVar.name === trimmed)
    ) {
      return false;
    }
    if (
      projectColors.some((colorVar) => colorVar.value === trimmed) ||
      craftspaceOnlyColors.some((colorVar) => colorVar.value === trimmed)
    ) {
      return false;
    }
    return true;
  };

  const isSelectedColorVar = (name: string, currentValue: string | undefined) => {
    if (!currentValue) return false;
    const trimmed = currentValue.trim();
    const varMatch = trimmed.match(/^var\(\s*(--[^)]+)\s*\)$/);
    if (varMatch) {
      return varMatch[1].replace(/^--/, "") === name;
    }
    if (trimmed.startsWith("--")) {
      return trimmed.replace(/^--/, "") === name;
    }
    return false;
  };

  const effectiveValue = hoverPreviewBaseValue ?? value;
  const localDisplayValue = hoverPreviewBaseValue ?? value;

  // Sync customColor and inputValue with value prop when it changes
  useEffect(() => {
    setCustomColor(value || "#ffffff");
    setInputValue(getDisplayNameForValue(value));
    setOpacityInput(String(Math.round(getAlphaFromColor(value) * 100)));
    if (!isLocalAdjusting) {
      setLocalPickerColor(getPickerBaseColor(value));
      setLocalPickerOpacity(getAlphaFromColor(value));
    }
  }, [value, isLocalAdjusting]);

  const canApplyOpacityToValue = (nextValue: string | undefined) => {
    if (!nextValue) return true;
    const trimmed = nextValue.trim();
    if (trimmed.toLowerCase().startsWith("color-mix(")) return false;
    if (trimmed.startsWith("var(") || trimmed.startsWith("--")) return false;
    if (resolveColorVariableReference(trimmed)) return false;
    return true;
  };

  const getAlphaFromColor = (color: string | undefined) => {
    if (!color) return 1;
    if (color.toLowerCase().startsWith("color-mix(")) {
      const mix = parseColorMixValue(color);
      if (mix) return Math.max(0, Math.min(1, mix.percent / 100));
    }
    if (color.startsWith("rgba(")) {
      const rgba = color.match(/rgba\(([^)]+)\)/);
      if (rgba) {
        const parts = rgba[1].split(",").map((x) => x.trim());
        const alpha = parseFloat(parts[3]);
        return Number.isFinite(alpha) ? alpha : 1;
      }
    }
    if (color.startsWith("#") && color.length === 9) {
      const alpha = parseInt(color.slice(7, 9), 16);
      return Number.isNaN(alpha) ? 1 : alpha / 255;
    }
    return 1;
  };

  const getPickerBaseColor = (color: string | undefined) => {
    if (!color) return "#ffffff";
    if (color.startsWith("#") || color.startsWith("rgb(") || color.startsWith("rgba(")) {
      return getColorWithoutAlpha(color);
    }
    try {
      const temp = document.createElement("div");
      temp.style.color = color;
      temp.style.visibility = "hidden";
      temp.style.position = "absolute";
      temp.style.top = "-9999px";
      document.body.appendChild(temp);
      const computed = window.getComputedStyle(temp).color;
      document.body.removeChild(temp);
      return getColorWithoutAlpha(computed);
    } catch (_error) {
      return "#ffffff";
    }
  };

  const getOpacityPercent = (color: string | undefined) =>
    Math.round(getAlphaFromColor(color) * 100);

  const parseHexInput = (rawInput: string) => {
    const input = rawInput.trim();
    if (!input) return null;
    const normalized = input.startsWith("#") ? input.slice(1) : input;
    if (/^[0-9A-Fa-f]{6}$/.test(normalized)) {
      return { hex: `#${normalized}`, alpha: null };
    }
    if (/^[0-9A-Fa-f]{8}$/.test(normalized)) {
      const r = parseInt(normalized.slice(0, 2), 16);
      const g = parseInt(normalized.slice(2, 4), 16);
      const b = parseInt(normalized.slice(4, 6), 16);
      const a = parseInt(normalized.slice(6, 8), 16) / 255;
      return { hex: `#${normalized.slice(0, 6)}`, alpha: a };
    }
    return null;
  };

  const syncHexInput = (color: string, setInput: (value: string) => void) => {
    if (!color) return;
    setInput(getColorWithoutAlpha(color).replace(/^#/, ""));
  };

  const applyHexInputToPicker = (
    rawInput: string,
    currentColor: string,
    currentOpacity: number,
    setColor: (value: string) => void,
    setOpacity: (value: number) => void,
    setInput: (value: string) => void,
    onColorApplied?: (value: string) => void,
  ) => {
    const parsed = parseHexInput(rawInput);
    if (!parsed) {
      setInput(getColorWithoutAlpha(currentColor).replace(/^#/, ""));
      return;
    }
    setColor(parsed.hex);
    if (parsed.alpha !== null) {
      setOpacity(parsed.alpha);
    }
    setInput(parsed.hex.replace(/^#/, ""));
    const nextColorValue =
      parsed.alpha !== null ? applyAlphaToHex(parsed.hex, parsed.alpha) : applyAlphaToHex(parsed.hex, currentOpacity);
    onColorApplied?.(nextColorValue);
  };

  const showHoverTooltip = (
    event: React.MouseEvent<HTMLElement>,
    name: string,
    colorValue: string,
  ) => {
    setHoverTooltip({
      visible: true,
      x: event.clientX + 12,
      y: event.clientY + 12,
      title: name,
      detail: `${colorValue} (${getOpacityPercent(colorValue)}%)`,
    });
  };

  const moveHoverTooltip = (event: React.MouseEvent<HTMLElement>) => {
    setHoverTooltip((prev) =>
      prev.visible
        ? { ...prev, x: event.clientX + 12, y: event.clientY + 12 }
        : prev,
    );
  };

  const hideHoverTooltip = () => {
    setHoverTooltip((prev) => ({ ...prev, visible: false }));
  };

  const startHoverPreview = (previewColor: string) => {
    if (hoverPreviewColor === previewColor) return;
    if (!hoverPreviewBaseValue) {
      setHoverPreviewBaseValue(value || "#ffffff");
    }
    setHoverPreviewColor(previewColor);
    onChange(previewColor);
  };

  const endHoverPreview = () => {
    onChange(hoverPreviewBaseValue ?? value ?? "#ffffff");
    setHoverPreviewBaseValue(null);
    setHoverPreviewColor(null);
  };

  const applyAlphaToHex = (color: string, alpha: number) => {
    if (!color.startsWith("#") || color.length < 7) return color;
    const hex = color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const toHex = (value: number) =>
      Math.round(value).toString(16).padStart(2, "0").toUpperCase();
    const alphaHex = toHex(Math.min(1, Math.max(0, alpha)) * 255);
    return `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`;
  };

  const handleColorSelect = (color: string) => {
    hideHoverTooltip();
    onChange(color);
    setCustomColor(color);
    setIsOpen(false);
  };

  // Helper function to render color swatch with transparency pattern
  const renderColorSwatch = (
    color: string,
    className: string = "w-12 h-10 rounded border border-gray-600",
  ) => {
    // Check for transparent colors (including rgba with alpha 0)
    const isTransparent =
      color.toLowerCase() === "transparent" ||
      color === "rgba(0,0,0,0)" ||
      color === "rgba(0, 0, 0, 0)" ||
      color === "hsla(0,0%,0%,0)" ||
      color === "hsla(0, 0%, 0%, 0)";

    if (isTransparent) {
      return (
        <div
          className={className}
          style={{
            backgroundImage: `
              linear-gradient(45deg, #e5e5e5 25%, transparent 25%),
              linear-gradient(-45deg, #e5e5e5 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #e5e5e5 75%),
              linear-gradient(-45deg, transparent 75%, #e5e5e5 75%)
            `,
            backgroundSize: "8px 8px",
            backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
            backgroundColor: "#ffffff",
          }}
        />
      );
    }

    return <div className={className} style={{ backgroundColor: color }} />;
  };

  const resolveColorVariableReference = (rawInput: string): string | null => {
    const input = rawInput.trim();
    if (!input) return null;

    const normalizedInput = input.toLowerCase();
    const allColors = [...projectColors, ...craftspaceColors];

    const directMatch = allColors.find(
      (colorVar) => colorVar.name.toLowerCase() === normalizedInput,
    );
    if (directMatch) {
      return toCSSVarReference(directMatch.name);
    }

    if (normalizedInput.startsWith("var(") || normalizedInput.startsWith("--")) {
      return input;
    }

    return null;
  };

  const isRecognizedColorInput = (rawInput: string): boolean => {
    const input = rawInput.trim();
    if (!input) return false;
    if (input.toLowerCase() === "transparent") return true;
    if (/^#[0-9A-Fa-f]{6}$/.test(input)) return true;
    if (/^#[0-9A-Fa-f]{8}$/.test(input)) return true;
    if (/^[0-9A-Fa-f]{6}$/.test(input)) return true;
    if (/^[0-9A-Fa-f]{8}$/.test(input)) return true;
    if (resolveColorVariableReference(input)) return true;
    if (input.startsWith("var(") || input.startsWith("--")) return true;
    try {
      if (typeof CSS !== "undefined" && CSS.supports("color", input)) {
        return true;
      }
    } catch (_error) {
      // Ignore and treat as unrecognized.
    }
    return false;
  };

  const handleCustomColorChange = (color: string) => {
    if (!isRecognizedColorInput(color)) {
      setInputValue(customColor);
      return;
    }

    // Handle transparent color
    if (color.toLowerCase() === "transparent") {
      const processedColor = "transparent";
      setCustomColor(processedColor);
      onChange(processedColor);
      return;
    }

    const variableReference = resolveColorVariableReference(color);
    if (variableReference) {
      setCustomColor(variableReference);
      setInputValue(getDisplayNameForValue(variableReference));
      onChange(variableReference);
      return;
    }

    // Auto-add hashtag if it's a valid hex color without one
    let processedColor = color;

    // Check for 6-digit hex
    if (/^[0-9A-Fa-f]{6}$/.test(color)) {
      processedColor = `#${color}`;
    }
    // Check for 8-digit hex with alpha (e.g., #00650080)
    else if (/^[0-9A-Fa-f]{8}$/.test(color)) {
      processedColor = `#${color}`;
    }
    // Check for hex with alpha that already has #
    else if (/^#[0-9A-Fa-f]{8}$/.test(color)) {
      processedColor = color;
    }
    // Check if it's already a valid 6-digit hex with #
    else if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
      processedColor = color;
    }

    setCustomColor(processedColor);
    setInputValue(getDisplayNameForValue(processedColor));
    onChange(processedColor);
  };

  const getNextColorName = (prefix: string, existingNames: Set<string>) => {
    let index = 1;
    while (existingNames.has(`${prefix}-${index}`)) {
      index += 1;
    }
    return `${prefix}-${index}`;
  };

  const normalizeColorVarName = (name: string) =>
    name.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-");

  const projectUsesCraftspaceColor = (
    projectToCheck: { data?: unknown },
    colorName: string | null,
  ) => {
    if (!colorName) return false;
    try {
      const haystack = JSON.stringify(projectToCheck.data || {});
      const normalized = normalizeColorVarName(colorName);
      return (
        haystack.includes(`var(--${normalized})`) ||
        haystack.includes(`--${normalized}`)
      );
    } catch (_error) {
      return false;
    }
  };

  const handleOpacityChange = (opacity: number) => {
    onOpacityChange?.(opacity);
  };

  const handleColorInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleCustomColorChange(inputValue);
      setIsEditing(false);
      setIsOpen(false);
      requestAnimationFrame(() => {
        const input = inputRef.current;
        if (!input) return;
        const end = input.value.length;
        input.setSelectionRange(end, end);
      });
    } else if (e.key === "Escape") {
      setIsEditing(false);
      setInputValue(customColor);
    }
  };

  const getAutocompleteMatch = (rawValue: string) => {
    const query = rawValue.trim().toLowerCase();
    if (!query) return null;
    const allColors = [...projectColors, ...craftspaceColors];
    return (
      allColors.find((item) => item.name.toLowerCase().startsWith(query)) || null
    );
  };

  const handleColorInputChange = (value: string, cursorPos: number | null) => {
    const match = getAutocompleteMatch(value);
    if (match && match.name.toLowerCase() !== value.trim().toLowerCase()) {
      setInputValue(match.name);
      requestAnimationFrame(() => {
        const input = inputRef.current;
        if (!input) return;
        const start = cursorPos ?? value.length;
        input.setSelectionRange(start, match.name.length);
      });
      return;
    }
    setInputValue(value);
  };

  // Extract color without alpha for the color input
  const getColorWithoutAlpha = (color: string | undefined): string => {
    if (!color || color === "transparent") {
      return "#000000"; // Return black for transparent in color input
    }

    if (color.startsWith("rgba(")) {
      const rgba = color.match(/rgba?\(([^)]+)\)/);
      if (rgba) {
        const [r, g, b] = rgba[1].split(",").map((x) => x.trim());
        // Convert RGB to hex for the color input
        const hex =
          "#" +
          [r, g, b]
            .map((x) => {
              const hex = parseInt(x, 10).toString(16);
              return hex.length === 1 ? `0${hex}` : hex;
            })
            .join("");
        return hex;
      }
    } else if (color.startsWith("rgb(")) {
      const rgb = color.match(/rgb\(([^)]+)\)/);
      if (rgb) {
        const [r, g, b] = rgb[1].split(",").map((x) => x.trim());
        // Convert RGB to hex for the color input
        const hex =
          "#" +
          [r, g, b]
            .map((x) => {
              const hex = parseInt(x, 10).toString(16);
              return hex.length === 1 ? `0${hex}` : hex;
            })
            .join("");
        return hex;
      }
    } else if (color.startsWith("#")) {
      // Return 6-digit hex (without alpha) for color input
      if (color.length === 9) {
        // 8-digit hex with alpha, return only RGB part
        return color.substring(0, 7);
      }
      return color;
    }
    return "#000000"; // fallback
  };

  // Get display value (with or without opacity)
  const getDisplayValue = (): string => {
    if (includeOpacity && onOpacityChange) {
      return `${value} (${Math.round((opacityValue || 1) * 100)}%)`;
    }
    return value;
  };

  const labelNode = (
    <div className="flex items-center gap-0.5">
      <Label
        className={`${tightSpacing ? "mb-1" : "mb-2"} cursor-pointer ${
          labelClassName || "text-gray-400"
        }`}
        onClick={(e) => {
          if ((e.altKey || e.metaKey) && onReset) {
            e.preventDefault();
            onReset();
          }
        }}
        onMouseEnter={onLabelMouseEnter}
        onMouseLeave={onLabelMouseLeave}
      >
        {label}
      </Label>
      {onReset && showReset && (
        <button
          type="button"
          className="inline-flex h-4 w-4 items-center justify-center mb-1 text-gray-400 hover:text-gray-200"
          title={`Clear ${label}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onReset();
          }}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );

  return (
    <div className={tightSpacing ? "space-y-1" : "space-y-2"}>
      {labelTooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{labelNode}</TooltipTrigger>
          <TooltipContent side="bottom">{labelTooltip}</TooltipContent>
        </Tooltip>
      ) : (
        labelNode
      )}
      <TooltipProvider>
        <Popover
          open={isOpen}
          onOpenChange={(open) => {
            if (!open && isMainPickerAdjusting) return;
            setIsOpen(open);
            if (open) {
              setIsLocalAdjusting(false);
              setLocalPickerColor(getPickerBaseColor(effectiveValue));
              setLocalPickerOpacity(getAlphaFromColor(effectiveValue));
              syncHexInput(getPickerBaseColor(effectiveValue), setLocalPickerHexInput);
            }
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2">
                <PopoverAnchor asChild>
                  <div className="flex items-center gap-0.5 w-full">
                    <button
                      type="button"
                      className={`flex items-center justify-center shrink-0 bg-gray-700 border-0 text-gray-400 hover:text-gray-200 ${
                        variant === "compact" ? "h-7 w-8 text-[10px]" : "h-10 w-10 text-sm"
                      } rounded-l-md`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setIsOpen((open) => !open);
                      }}
                      aria-label="Toggle color list"
                    >
                      {renderColorSwatch(
                        value,
                        `rounded ${variant === "compact" ? "w-3 h-3" : "w-4 h-4"}`,
                      )}
                    </button>
                    <div className="relative flex-1 min-w-0">
                      <Input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) =>
                          handleColorInputChange(
                            e.target.value,
                            e.currentTarget.selectionStart,
                          )
                        }
                        onKeyDown={handleColorInputKeyDown}
                        onFocus={(e) => {
                          setIsEditing(true);
                          e.target.select();
                        }}
                        onBlur={() => {
                          setIsEditing(false);
                          if (
                            value &&
                            value.trim().toLowerCase().startsWith("color-mix(") &&
                            inputValue.trim() === getDisplayNameForValue(value)
                          ) {
                            return;
                          }
                          handleCustomColorChange(inputValue);
                        }}
                        placeholder="000000"
                        className={`w-full bg-gray-700 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus:ring-0 outline-none text-white rounded-l-none rounded-r-none ${
                          variant === "compact"
                            ? "h-7 pl-2 pr-2 text-xs"
                            : "h-10 pl-3 pr-3"
                        }`}
                        title="Type hex color (#000000) or color name. Press Enter."
                      />
                    </div>
                    <div className="relative shrink-0">
                        <Input
                          type="text"
                          value={opacityInput}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, "");
                            setOpacityInput(raw);
                          }}
                          onBlur={() => {
                            const nextPercent = Math.max(
                              0,
                              Math.min(100, opacityInput === "" ? 100 : parseInt(opacityInput, 10)),
                            );
                            const nextOpacity = nextPercent / 100;
                            if (!canApplyOpacityToValue(value)) {
                              if (onOpacityChange) {
                                onOpacityChange(nextOpacity);
                                setOpacityInput(String(nextPercent));
                                return;
                              }
                              const mix = value ? parseColorMixValue(value) : null;
                              let baseColor =
                                mix?.base ||
                                resolveColorVariableReference(value || "") ||
                                value ||
                                "#000000";
                              if (
                                baseColor &&
                                !(baseColor.startsWith("var(") || baseColor.startsWith("--"))
                              ) {
                                const normalizedVar = resolveColorVariableReference(baseColor);
                                if (normalizedVar) baseColor = normalizedVar;
                              }
                              const percent = Math.round(nextOpacity * 100);
                              onChange(
                                `color-mix(in srgb, ${baseColor} ${percent}%, transparent)`,
                              );
                              setOpacityInput(String(percent));
                              return;
                            }
                            const baseColor = getPickerBaseColor(value || "#000000");
                            onChange(applyAlphaToHex(baseColor, nextOpacity));
                            setOpacityInput(String(nextPercent));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.currentTarget.blur();
                            }
                          }}
                          placeholder="100"
                          className={`bg-gray-700 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus:ring-0 outline-none text-white rounded-r-md rounded-l-none text-center ${
                            variant === "compact"
                              ? "h-7 w-14 text-[10px] pl-2 pr-4"
                              : "h-10 w-16 text-xs pl-2 pr-5"
                        }`}
                          title="Opacity (%)"
                        />
                      <span
                        className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none ${
                          variant === "compact" ? "text-[10px]" : "text-xs"
                        }`}
                      >
                        %
                      </span>
                    </div>
                  </div>
                </PopoverAnchor>
              </div>
            </TooltipTrigger>
          </Tooltip>
          <PopoverContent
            className="z-[10000] w-80 max-w-[calc(100vw-180px)] p-4 bg-gray-800 border-gray-700"
            onPointerDown={() => setIsMainPickerAdjusting(true)}
            onPointerUp={() => setIsMainPickerAdjusting(false)}
            onPointerLeave={() => setIsMainPickerAdjusting(false)}
          >
            <div className="space-y-4">
              <div className="space-y-4">
                <div>
                  <div
                    className="space-y-3"
                    onPointerDown={() => setIsLocalAdjusting(true)}
                    onPointerUp={() => setIsLocalAdjusting(false)}
                    onPointerLeave={() => setIsLocalAdjusting(false)}
                  >
                    <HexColorPicker
                      className="colorpicker-wide w-full"
                      color={localPickerColor}
                      onChange={(nextColor) => {
                        setLocalPickerColor(nextColor);
                        syncHexInput(nextColor, setLocalPickerHexInput);
                        onChange(applyAlphaToHex(nextColor, localPickerOpacity));
                      }}
                    />
                    <Input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={localPickerOpacity}
                      onChange={(e) => {
                        setIsLocalAdjusting(true);
                        const nextOpacity = parseFloat(e.target.value);
                        setLocalPickerOpacity(nextOpacity);
                        onChange(applyAlphaToHex(localPickerColor, nextOpacity));
                      }}
                      onMouseUp={() => setIsLocalAdjusting(false)}
                      onTouchEnd={() => setIsLocalAdjusting(false)}
                      className="colorpicker-slider colorpicker-slider--alpha cursor-pointer"
                      style={{
                        ["--alpha-gradient" as string]: `linear-gradient(to right, #e5e7eb 0%, #e5e7eb ${localPickerOpacity * 100}%, #4b5563 ${localPickerOpacity * 100}%, #4b5563 100%)`,
                      }}
                    />
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-300">Color</Label>
                      <div className="relative">
                        <div className="absolute left-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded border border-gray-500">
                          {renderColorSwatch(
                            applyAlphaToHex(localPickerColor, localPickerOpacity),
                            "h-full w-full rounded",
                          )}
                        </div>
                        <Input
                          value={localPickerHexInput}
                          onChange={(e) => setLocalPickerHexInput(e.target.value)}
                          onBlur={() => {
                            applyHexInputToPicker(
                              localPickerHexInput,
                              localPickerColor,
                              localPickerOpacity,
                              setLocalPickerColor,
                              setLocalPickerOpacity,
                              setLocalPickerHexInput,
                              (nextColor) => onChange(nextColor),
                            );
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              applyHexInputToPicker(
                                localPickerHexInput,
                                localPickerColor,
                                localPickerOpacity,
                                setLocalPickerColor,
                                setLocalPickerOpacity,
                                setLocalPickerHexInput,
                                (nextColor) => onChange(nextColor),
                              );
                            }
                          }}
                          placeholder="ffffff or #ffffff"
                          className="h-8 bg-gray-700 border-gray-600 text-white text-xs pl-10"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {!hideColorVariables && projectColors.length > 0 && (
                  <div>
                    {projectId ? (
                      <Link
                        to={`/${projectId}/variables`}
                        className="text-[#d1d5db] mb-1 block text-xs hover:text-white hover:underline underline-offset-2"
                      >
                        Project Variables
                      </Link>
                    ) : (
                      <span className="text-[#d1d5db] mb-1 block text-xs">
                        Project Variables
                      </span>
                    )}
                    <div className="grid grid-cols-6 gap-x-1 gap-y-4 w-full">
                      {projectColors.map((colorVar) => (
                        <div
                          key={colorVar.id}
                          className={`w-10 h-10 rounded border border-gray-600 overflow-visible relative group transition-all duration-150 ease-out ${
                            isSelectedColorVar(colorVar.name, effectiveValue)
                              ? "ring-2 ring-blue-400"
                              : ""
                          }`}
                          onMouseLeave={() => {
                            hideHoverTooltip();
                            endHoverPreview();
                          }}
                          onPointerLeave={() => {
                            hideHoverTooltip();
                            endHoverPreview();
                          }}
                        >
                          <div className="absolute inset-0 rounded overflow-hidden">
                            {renderColorSwatch(colorVar.value, "absolute inset-0")}
                          </div>
                          <button
                            type="button"
                            className="absolute inset-0 focus:outline-none"
                            onClick={() => handleColorSelect(toCSSVarReference(colorVar.name))}
                            aria-label={`Select ${colorVar.name}`}
                            onMouseEnter={(event) =>
                              showHoverTooltip(event, colorVar.name, colorVar.value)
                            }
                            onMouseMove={moveHoverTooltip}
                            onMouseLeave={() => {
                              hideHoverTooltip();
                              endHoverPreview();
                            }}
                            onPointerEnter={(event) => {
                              showHoverTooltip(event, colorVar.name, colorVar.value);
                              startHoverPreview(colorVar.value);
                            }}
                            onPointerMove={moveHoverTooltip}
                            onPointerLeave={() => {
                              hideHoverTooltip();
                              endHoverPreview();
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!hideColorVariables && craftspaceOnlyColors.length > 0 && (
                  <div>
                    {project?.craftspace_id ? (
                      <Link
                        to="/dashboard?p=colors"
                        state={{
                          activeView: "variables-colors",
                          craftspaceId: project.craftspace_id,
                        }}
                        className="text-[#d1d5db] mb-1 block text-xs hover:text-white hover:underline underline-offset-2"
                      >
                        Craftspace Variables
                      </Link>
                    ) : (
                      <span className="text-[#d1d5db] mb-1 block text-xs">
                        Craftspace Variables
                      </span>
                    )}
                    <div className="grid grid-cols-6 gap-x-1 gap-y-4 w-full">
                      {craftspaceOnlyColors.map((colorVar) => (
                        <div
                          key={`ws-${colorVar.id}`}
                          className={`w-10 h-10 rounded border border-gray-600 overflow-visible relative group transition-all duration-150 ease-out ${
                            isSelectedColorVar(colorVar.name, effectiveValue)
                              ? "ring-2 ring-blue-400"
                              : ""
                          }`}
                          onMouseLeave={() => {
                            hideHoverTooltip();
                            endHoverPreview();
                          }}
                          onPointerLeave={() => {
                            hideHoverTooltip();
                            endHoverPreview();
                          }}
                        >
                          <div className="absolute inset-0 rounded overflow-hidden">
                            {renderColorSwatch(colorVar.value, "absolute inset-0")}
                          </div>
                          <button
                            type="button"
                            className="absolute inset-0 focus:outline-none"
                            onClick={() => handleColorSelect(toCSSVarReference(colorVar.name))}
                            aria-label={`Select ${colorVar.name}`}
                            onMouseEnter={(event) =>
                              showHoverTooltip(event, colorVar.name, colorVar.value)
                            }
                            onMouseMove={moveHoverTooltip}
                            onMouseLeave={() => {
                              hideHoverTooltip();
                              endHoverPreview();
                            }}
                            onPointerEnter={(event) => {
                              showHoverTooltip(event, colorVar.name, colorVar.value);
                              startHoverPreview(colorVar.value);
                            }}
                            onPointerMove={moveHoverTooltip}
                            onPointerLeave={() => {
                              hideHoverTooltip();
                              endHoverPreview();
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preset Colors removed by request */}
              </div>

              {includeOpacity && onOpacityChange && (
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Opacity</Label>
                  <div className="space-y-2">
                    <div className="relative">
                      <Input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={internalOpacity}
                        onChange={(e) => {
                          const newOpacity = parseFloat(e.target.value);
                          console.log("Opacity changed to:", newOpacity); // Debug log
                          setInternalOpacity(newOpacity); // Update internal state immediately
                          handleOpacityChange(newOpacity); // Call parent callback
                        }}
                        className="colorpicker-slider cursor-pointer relative z-10"
                        style={{
                          background: `linear-gradient(to right, #e5e7eb 0%, #e5e7eb ${internalOpacity * 100}%, #4b5563 ${internalOpacity * 100}%, #4b5563 100%)`,
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-center">
                      <Input
                        type="text"
                        value={`${Math.round(internalOpacity * 100)}`}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, "");
                          const nextPercent = Math.max(
                            0,
                            Math.min(100, raw === "" ? 0 : parseInt(raw, 10)),
                          );
                          const nextOpacity = nextPercent / 100;
                          setInternalOpacity(nextOpacity);
                          handleOpacityChange(nextOpacity);
                        }}
                        onBlur={(e) => {
                          if (e.target.value.trim() === "") {
                            const nextOpacity = 0;
                            setInternalOpacity(nextOpacity);
                            handleOpacityChange(nextOpacity);
                          }
                        }}
                        className="w-12 text-center text-xs bg-transparent border-none text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 h-5"
                        aria-label="Opacity percent"
                      />
                      <span className="text-xs text-gray-400">%</span>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </PopoverContent>
        </Popover>
      </TooltipProvider>
      {hoverTooltip.visible &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed z-[99999] pointer-events-none"
            style={{ left: hoverTooltip.x, top: hoverTooltip.y }}
          >
              <div className="bg-gray-900 border border-gray-700 text-white text-xs rounded-md px-2 py-1 shadow-lg">
              <div className="font-medium">{hoverTooltip.title}</div>
              <div className="text-gray-300">{hoverTooltip.detail}</div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export const ColorPicker = React.memo(ColorPickerComponent);
